// Maps Google Ads RecommendationType enum values to UI metadata.
//
// Two categories:
//   - 'covered': Releva exposes the underlying field — clicking switches the
//                user to the campaign settings tab so they can edit there.
//   - 'fyi':     Releva has no UI control — they need to action it in
//                Google Ads directly.
//
// All summaries are functions of (rec, { currency }) so we can interpolate
// currency-aware values without branching on type. Unmapped types fall
// through to a generic 'fyi' card via getRecommendationMeta().
//
// Account-scoped recommendation types (MOVE_UNUSED_BUDGET,
// REFRESH_CUSTOMER_MATCH_LIST, PERFORMANCE_MAX_OPT_IN, the *_TO_PERFORMANCE_MAX
// migration types) are deliberately NOT mapped — the per-campaign GAQL
// filter in listRecommendations() never matches them. Add them back when /
// if a domain-level recommendations view exists.
//
// See https://developers.google.com/google-ads/api/reference/rpc/v23/RecommendationTypeEnum.RecommendationType

const ONE_UNIT = 1_000_000;

const microsToUnit = (micros) => {
  if (micros == null) return null;
  const num = Number(micros);
  return Number.isNaN(num) ? null : num / ONE_UNIT;
};

const formatCurrency = (micros, currency) => {
  const value = microsToUnit(micros);
  if (value == null) return null;
  if (!currency) return value.toFixed(2);
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
  } catch {
    return value.toFixed(2);
  }
};

// `budgetOptions` is an unordered list of suggested budgets (typically
// low / recommended / high). We rely on the backend to pre-filter or sort
// so that index 0 is the recommended option — if it ever stops doing that,
// the UI will surface a non-recommended budget. Same caveat applies to
// `marginalRoiCampaignBudgetRecommendation.budgetOptions` below.
const firstBudgetOption = (rec) => rec?.campaignBudgetRecommendation?.budgetOptions?.[0];

// We don't have Google's obfuscated customer id (`ocid`) — the raw customer
// id isn't a valid substitute (passing it routes the user to their default
// account, not the suggested one). Link to the generic recommendations
// surface; Google Ads picks the account the user is logged into.
const googleAdsRecommendationsUrl = () => 'https://ads.google.com/aw/recommendations';

const RECOMMENDATION_META = {
  CAMPAIGN_BUDGET: {
    category: 'covered',
    title: 'Raise daily budget',
    summary: (rec, { currency }) => {
      const option = firstBudgetOption(rec);
      const suggested = formatCurrency(option?.budgetAmountMicros, currency);
      const current = formatCurrency(rec?.campaignBudgetRecommendation?.currentBudgetAmountMicros, currency);
      if (suggested && current) {
        return `Google suggests raising the daily budget from ${current} to ${suggested} to capture more conversions.`;
      }
      return 'Google suggests raising this campaign’s daily budget.';
    },
  },
  FORECASTING_CAMPAIGN_BUDGET: {
    category: 'covered',
    title: 'Adjust daily budget (forecast)',
    summary: (rec, { currency }) => {
      const option = firstBudgetOption(rec);
      const suggested = formatCurrency(option?.budgetAmountMicros, currency);
      return suggested
        ? `Forecast suggests a daily budget of ${suggested} based on similar accounts.`
        : 'Forecast suggests adjusting this campaign’s daily budget.';
    },
  },
  RAISE_TARGET_CPA: {
    category: 'covered',
    title: 'Raise target CPA',
    summary: () => 'Your target CPA is below the median for similar campaigns and may be limiting delivery.',
  },
  LOWER_TARGET_CPA: {
    category: 'covered',
    title: 'Lower target CPA',
    summary: () => 'Your target CPA may be too high; Google suggests lowering it to improve efficiency.',
  },
  TARGET_CPA_OPT_IN: {
    category: 'covered',
    title: 'Switch bid strategy to Target CPA',
    summary: (rec, { currency }) => {
      const suggested = formatCurrency(rec?.targetCpaOptInRecommendation?.recommendedTargetCpaMicros, currency);
      return suggested
        ? `Google suggests Target CPA bidding with a target of ${suggested}.`
        : 'Google suggests switching this campaign to Target CPA bidding.';
    },
  },
  FORECASTING_SET_TARGET_CPA: {
    category: 'covered',
    title: 'Set target CPA (forecast)',
    summary: () => 'Forecast suggests setting a target CPA on this campaign.',
  },
  RAISE_TARGET_ROAS: {
    category: 'covered',
    title: 'Raise target ROAS',
    summary: () => 'Your target ROAS may be too low for current performance.',
  },
  LOWER_TARGET_ROAS: {
    category: 'covered',
    title: 'Lower target ROAS',
    summary: (rec) => {
      const suggested = rec?.lowerTargetRoasRecommendation?.recommendedTargetRoas;
      return suggested
        ? `Google suggests lowering target ROAS to ${(suggested * 100).toFixed(0)}% to grow conversion value.`
        : 'Google suggests lowering this campaign’s target ROAS.';
    },
  },
  TARGET_ROAS_OPT_IN: {
    category: 'covered',
    title: 'Switch bid strategy to Target ROAS',
    summary: (rec) => {
      const suggested = rec?.targetRoasOptInRecommendation?.recommendedTargetRoas;
      return suggested
        ? `Google suggests Target ROAS bidding at ${(suggested * 100).toFixed(0)}%.`
        : 'Google suggests switching this campaign to Target ROAS bidding.';
    },
  },
  FORECASTING_SET_TARGET_ROAS: {
    category: 'covered',
    title: 'Set target ROAS (forecast)',
    summary: () => 'Forecast suggests setting a target ROAS on this campaign.',
  },
  MAXIMIZE_CONVERSIONS_OPT_IN: {
    category: 'covered',
    title: 'Switch to Maximize Conversions',
    summary: () => 'Google suggests switching this campaign to Maximize Conversions bidding.',
  },
  MAXIMIZE_CONVERSION_VALUE_OPT_IN: {
    category: 'covered',
    title: 'Switch to Maximize Conversion Value',
    summary: () => 'Google suggests switching this campaign to Maximize Conversion Value bidding.',
  },
  IMPROVE_PERFORMANCE_MAX_AD_STRENGTH: {
    category: 'covered',
    title: 'Improve ad strength',
    summary: () => 'One or more asset groups have low ad strength. Add or replace assets to improve performance.',
  },
  RAISE_TARGET_CPA_BID_TOO_LOW: {
    category: 'covered',
    title: 'Raise target CPA (bids too low)',
    summary: (rec) => {
      const multiplier = rec?.raiseTargetCpaBidTooLowRecommendation?.recommendedTargetMultiplier;
      return multiplier && multiplier > 1
        ? `Your target CPA is too restrictive to drive conversions — Google suggests raising it by ~${Math.round((multiplier - 1) * 100)}%.`
        : 'Your target CPA is too restrictive to drive conversions — raising it will let the campaign deliver.';
    },
  },
  SET_TARGET_CPA: {
    category: 'covered',
    title: 'Set target CPA',
    summary: (rec, { currency }) => {
      const suggested = formatCurrency(rec?.targetCpaOptInRecommendation?.recommendedTargetCpaMicros, currency);
      return suggested
        ? `This campaign has no target CPA — Google suggests setting it to ${suggested}.`
        : 'This campaign has no target CPA — Google suggests setting one.';
    },
  },
  SET_TARGET_ROAS: {
    category: 'covered',
    title: 'Set target ROAS',
    summary: (rec) => {
      const suggested = rec?.targetRoasOptInRecommendation?.recommendedTargetRoas;
      return suggested
        ? `This campaign has no target ROAS — Google suggests setting it to ${(suggested * 100).toFixed(0)}%.`
        : 'This campaign has no target ROAS — Google suggests setting one.';
    },
  },
  MARGINAL_ROI_CAMPAIGN_BUDGET: {
    category: 'covered',
    title: 'Adjust budget for better ROI',
    summary: (rec, { currency }) => {
      const option = rec?.marginalRoiCampaignBudgetRecommendation?.budgetOptions?.[0];
      const suggested = formatCurrency(option?.budgetAmountMicros, currency);
      return suggested
        ? `Adjusting daily budget to ${suggested} would capture conversions with better marginal ROI.`
        : 'Adjusting this campaign’s budget would improve marginal ROI.';
    },
  },

  SHOPPING_FIX_DISAPPROVED_PRODUCTS: {
    category: 'fyi',
    title: 'Fix disapproved products',
    summary: (rec) => {
      const disapproved = rec?.shoppingFixDisapprovedProductsRecommendation?.disapprovedProductsCount;
      const total = rec?.shoppingFixDisapprovedProductsRecommendation?.productsCount;
      if (disapproved != null && total != null) {
        return `${disapproved} of ${total} products in your Merchant Center feed are disapproved and aren’t serving. Resolve them in Merchant Center.`;
      }
      return 'Some products in your Merchant Center feed are disapproved and aren’t serving. Resolve them in Merchant Center.';
    },
  },
  SHOPPING_FIX_MERCHANT_CENTER_ACCOUNT_SUSPENSION_WARNING: {
    category: 'fyi',
    title: 'Merchant Center suspension warning',
    summary: () =>
      'Your Merchant Center account has issues that may lead to suspension. Resolve them in Merchant Center to avoid disruption.',
  },

  PERFORMANCE_MAX_FINAL_URL_OPT_IN: {
    category: 'fyi',
    title: 'Enable Final URL Expansion',
    summary: () => 'Let Google match your ads to relevant landing pages beyond the ones you’ve supplied.',
  },
  SEARCH_PARTNERS_OPT_IN: {
    category: 'fyi',
    title: 'Opt in to Search Partners',
    summary: () => 'Expand reach by showing ads on Google’s search partner network.',
  },
};

const getRecommendationMeta = (rec, { currency } = {}) => {
  const meta = rec?.type ? RECOMMENDATION_META[rec.type] : null;
  if (meta) {
    return {
      category: meta.category,
      title: meta.title,
      summary: meta.summary(rec, { currency }),
    };
  }
  return {
    category: 'fyi',
    title: rec?.type ? rec.type.replace(/_/g, ' ') : 'Recommendation',
    summary: 'Google has a suggestion for this campaign. Open it in Google Ads for details.',
  };
};

export { RECOMMENDATION_META, getRecommendationMeta, formatCurrency, googleAdsRecommendationsUrl };
